import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import WorldMap, { BranchDetailCard } from "@/components/WorldMap";
import { Phone, Mail, MessageCircle, MapPin, Upload, Send } from "lucide-react";
import { waLink, quoteMessage } from "@/lib/whatsapp";

function PageHeader({ eyebrow, title, subtitle, image }) {
  return (
    <section className="relative min-h-[52vh] bg-pipeco-dark text-white flex items-end overflow-hidden">
      {image && (<><img src={image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45"/><div className="absolute inset-0 bg-gradient-to-t from-pipeco-dark via-black/60 to-transparent"/></>)}
      <div className="relative container-x px-6 md:px-10 py-24 md:py-32">
        <div className="overline text-emerald-300">{eyebrow}</div>
        <h1 className="headline text-5xl md:text-7xl mt-4 max-w-4xl">{title}</h1>
        {subtitle && <p className="text-white/70 mt-5 max-w-2xl">{subtitle}</p>}
      </div>
    </section>
  );
}

export function GlobalPresence() {
  const [branches, setBranches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [country, setCountry] = useState("All");
  useEffect(() => { api.get("/branches").then(r => setBranches(r.data)); }, []);
  const countries = useMemo(() => ["All", ...Array.from(new Set(branches.map(b=>b.country)))], [branches]);
  const filtered = country === "All" ? branches : branches.filter(b => b.country === country);
  return (
    <div data-testid="global-presence-page">
      <PageHeader eyebrow="Global Presence · Since 2010" title="One Group. Every Region." subtitle="26 branches across 22 countries — 10,000+ engineered water storage projects delivered worldwide." image="/tanks/shared-page-hero.webp"/>
      <section className="global-presence-stats-section bg-pipeco-dark text-white py-14">
        <div className="container-x px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-10 border-y border-white/10 py-8 md:py-10">
          <div><div className="font-display text-4xl md:text-6xl font-black text-emerald-300 leading-none">26</div><div className="overline text-white/50 mt-2 md:mt-3 text-[10px] md:text-xs">Global Branches</div></div>
          <div><div className="font-display text-4xl md:text-6xl font-black text-emerald-300 leading-none">22</div><div className="overline text-white/50 mt-2 md:mt-3 text-[10px] md:text-xs">Countries</div></div>
          <div><div className="font-display text-4xl md:text-6xl font-black text-emerald-300 leading-none">10K+</div><div className="overline text-white/50 mt-2 md:mt-3 text-[10px] md:text-xs">Projects Delivered</div></div>
          <div><div className="font-display text-4xl md:text-6xl font-black text-emerald-300 leading-none">2010</div><div className="overline text-white/50 mt-2 md:mt-3 text-[10px] md:text-xs">Operating Since</div></div>
        </div>
      </section>
      <section className="global-presence-map-section section-pad bg-pipeco-dark text-white pt-14">
        <div className="global-presence-map-container container-x px-6 md:px-10">
          <WorldMap branches={branches} onSelect={setSelected} globalPresenceMap/>
        </div>
      </section>
      <section className="section-pad bg-white">
        <div className="container-x px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div>
              <div className="overline text-pipeco-green">Our Offices</div>
              <h2 className="global-offices-heading headline text-3xl md:text-4xl mt-2 text-slate-900">Find your local PIPECO team</h2>
            </div>
            <select data-testid="branch-country-filter" value={country} onChange={(e)=>setCountry(e.target.value)} className="border border-black/10 px-4 py-3 text-sm">
              {countries.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map(b => (
              <div key={b.id} data-testid={`branch-card-${b.country_code}`} className="bg-white border border-black/5 card-lift">
                <div className="aspect-16-9 overflow-hidden bg-slate-100">{b.main_image && <img src={b.main_image} alt={b.name} className="w-full h-full object-cover"/>}</div>
                <div className="p-6">
                  <div className="overline text-pipeco-green">{b.branch_type}</div>
                  <h3 className="font-display text-xl mt-2 text-slate-900">{b.name}</h3>
                  <div className="text-sm text-slate-500 mt-1">{b.city}, {b.country}</div>
                  {b.address && <div className="text-xs text-slate-500 mt-3">{b.address}</div>}
                  <button onClick={()=>setSelected(b)} className="btn-outline-dark mt-5 w-full justify-center">View Details</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {selected && <BranchDetailCard branch={selected} onClose={()=>setSelected(null)}/>}
    </div>
  );
}

export function Contact() {
  const [sp] = useSearchParams();
  const [branches, setBranches] = useState([]);
  const [country, setCountry] = useState(sp.get("country") || "All");
  const [selected, setSelected] = useState(null);
  const [emailPopup, setEmailPopup] = useState("");
  const [desktopEmailActions, setDesktopEmailActions] = useState(() => window.matchMedia("(min-width: 768px)").matches);
  const [form, setForm] = useState({ name: "", company: "", country: sp.get("country") || "", email: "", phone: "", whatsapp: "", product: sp.get("product") || "", capacity: "", project_type: "", project_location: "", message: "" });
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { api.get("/branches").then(r => setBranches(r.data)); }, []);
  useEffect(() => {
    const desktopViewport = window.matchMedia("(min-width: 768px)");
    const updateEmailAction = () => {
      setDesktopEmailActions(desktopViewport.matches);
      if (!desktopViewport.matches) setEmailPopup("");
    };
    desktopViewport.addEventListener("change", updateEmailAction);
    return () => desktopViewport.removeEventListener("change", updateEmailAction);
  }, []);
  const countries = useMemo(() => ["All", ...Array.from(new Set(branches.map(b => b.country)))], [branches]);
  const filtered = country === "All" ? branches : branches.filter(b => b.country === country);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setSent(false);
    try {
      await api.post("/enquiries", form);
      setSent(true);
      setForm({ ...form, name: "", email: "", phone: "", message: "" });
    } catch (e) {
      setErr(String(e.response?.data?.detail || e.message));
    }
  };

  const copyBranchEmail = async () => {
    if (navigator.clipboard) await navigator.clipboard.writeText(emailPopup);
  };

  return (
    <div data-testid="contact-page">
      <PageHeader eyebrow="Contact" title="Get in Touch with PIPECO." subtitle="Reach the nearest PIPECO office or send us your project enquiry." image="/tanks/shared-page-hero.webp"/>
      <section className="section-pad bg-white">
        <div className="container-x px-6 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="overline text-pipeco-green">Global Offices</div>
            <h2 className="headline text-3xl md:text-4xl mt-2 text-slate-900 mb-6">Choose your country</h2>
            <select data-testid="contact-country-filter" value={country} onChange={(e)=>setCountry(e.target.value)} className="border border-black/10 px-4 py-3 text-sm mb-6 w-full max-w-xs">
              {countries.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="space-y-4">
              {filtered.map(b => (
                <div key={b.id} data-testid={`contact-branch-${b.country_code}`} className="border border-black/5 p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="overline text-pipeco-green">{b.branch_type}</div>
                      <h3 className="font-display text-xl mt-1 text-slate-900">{b.name}</h3>
                      <div className="text-sm text-slate-500">{b.city}, {b.country}</div>
                      {b.address && <div className="text-sm text-slate-700 mt-3">{b.address}</div>}
                    </div>
                    <button onClick={()=>setSelected(b)} className="text-[11px] uppercase tracking-widest text-pipeco-green hover-line shrink-0">View →</button>
                  </div>
                  <div className="grid grid-cols-4 gap-2 mt-5">
                    <a href={`tel:${b.phone}`} className="btn-outline-dark justify-center text-[10px]"><Phone size={12}/></a>
                    <a href={waLink(b.whatsapp || b.phone, `Hello PIPECO GROUP,\n\nI’m interested in your *water storage tanks* for my project. Could you please share more details and advise on the most suitable option?\n\n*Page reference:*\n${window.location.href}\n\nThank you.`)} target="_blank" rel="noreferrer" className="btn-outline-dark justify-center text-[10px] !border-[color:var(--wa-green)] !text-[color:var(--wa-green)]"><MessageCircle size={12}/></a>
                    {desktopEmailActions ? <button type="button" data-testid={`contact-branch-email-${b.country_code}`} onClick={()=>setEmailPopup(b.email?.trim() || "")} className="btn-outline-dark justify-center text-[10px] relative z-10 pointer-events-auto"><Mail size={12}/></button> : <a data-testid={`contact-branch-email-${b.country_code}`} href={b.email ? `mailto:${b.email.trim()}` : undefined} className="btn-outline-dark justify-center text-[10px]"><Mail size={12}/></a>}
                    <a href={b.maps_link || `https://www.google.com/maps/search/${b.lat},${b.lng}`} target="_blank" rel="noreferrer" className="btn-outline-dark justify-center text-[10px]"><MapPin size={12}/></a>
                  </div>
                </div>
              ))}
              {filtered.length === 0 && <div className="p-6 bg-slate-50 border text-sm text-slate-600">No office listed for this country yet. Use the enquiry form and we'll route your request.</div>}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-pipeco-dark text-white p-8 md:p-10">
              <div className="overline text-emerald-300">Project Enquiry</div>
              <h3 className="headline text-3xl mt-2">Send us your brief</h3>
              <form onSubmit={submit} data-testid="enquiry-form" className="mt-6 grid gap-3">
                {[["name","Full Name",true],["company","Company"],["country","Country"],["email","Email",true],["phone","Phone"],["whatsapp","WhatsApp"],["product","Product Required"],["capacity","Capacity (m³)"],["project_type","Project Type"],["project_location","Project Location"]].map(([k,l,req]) => (
                  <input key={k} data-testid={`enquiry-${k}`} required={req} value={form[k]} onChange={(e)=>setForm({...form,[k]:e.target.value})} placeholder={l} className="w-full bg-transparent border-b border-white/20 py-3 text-sm placeholder:text-white/40 focus:outline-none focus:border-emerald-300"/>
                ))}
                <textarea required data-testid="enquiry-message" value={form.message} onChange={(e)=>setForm({...form,message:e.target.value})} rows={4} placeholder="Message / Requirements" className="w-full bg-transparent border-b border-white/20 py-3 text-sm placeholder:text-white/40 focus:outline-none focus:border-emerald-300 resize-none"/>
                <label className="flex items-center gap-2 text-xs text-white/50 mt-2 cursor-pointer"><Upload size={14}/> Attach drawing / BOQ / spec (contact us for upload link)</label>
                {err && <div className="text-red-300 text-sm">{err}</div>}
                {sent && <div data-testid="enquiry-success" className="text-emerald-300 text-sm">Thank you — your enquiry has been received. PIPECO will contact you shortly.</div>}
                <button data-testid="enquiry-submit" type="submit" className="btn-primary justify-center mt-3 !bg-emerald-500 !border-emerald-500 hover:!bg-emerald-400"><Send size={14}/>Send Enquiry</button>
              </form>
            </div>
          </div>
        </div>
      </section>
      {emailPopup && <div data-testid="branch-email-popover" className="fixed inset-0 z-[60] bg-black/30 grid place-items-center p-4" onClick={()=>setEmailPopup("")}>
        <div className="w-full max-w-[20rem] bg-slate-50 border border-slate-200 shadow-xl p-5" onClick={e=>e.stopPropagation()}>
          <div className="overline text-[9px] tracking-[0.18em] text-pipeco-green">Branch Email</div>
          <div data-testid="branch-email-popover-address" className="text-slate-800 text-sm font-medium mt-2.5 break-all">{emailPopup}</div>
          <div className="grid grid-cols-2 gap-2 mt-5">
            <button type="button" data-testid="copy-branch-email" onClick={copyBranchEmail} className="btn-outline-dark justify-center text-[11px] px-3 py-2">Copy Email</button>
            <a data-testid="open-branch-email-app" href={`mailto:${emailPopup}`} className="btn-primary justify-center text-[11px] px-3 py-2 !bg-[#0F5A46] !border-[#0F5A46]">Open Email App</a>
          </div>
        </div>
      </div>}
      {selected && <BranchDetailCard branch={selected} onClose={()=>setSelected(null)}/>}
    </div>
  );
}

export default GlobalPresence;
